import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { MessageCircle, Send, Phone, Users, Eye, EyeOff, Ticket, X, Circle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const presenceColor = {
  available: 'bg-green-500',
  busy: 'bg-yellow-500',
  donotdisturb: 'bg-red-500',
  away: 'bg-yellow-400',
  berightback: 'bg-yellow-400',
  offline: 'bg-gray-400',
  presenceunknown: 'bg-gray-300',
  unknown: 'bg-gray-300',
};
const presenceLabel = {
  available: 'Available',
  busy: 'Busy',
  donotdisturb: 'Do Not Disturb',
  away: 'Away',
  berightback: 'Be Right Back',
  offline: 'Offline',
  presenceunknown: 'Teams Inactive',
  unknown: 'Unknown',
};

export default function TeamsChat() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [incognito, setIncognito] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [messagesError, setMessagesError] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    loadData();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (activeChat) {
      loadMessages(activeChat.id);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => loadMessages(activeChat.id), 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeChat?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadData = async () => {
    setLoadError(false);
    try {
      const [staffRes, chatsRes, incogRes] = await Promise.allSettled([
        api.get('/teams/presence'),
        api.get('/teams/chats'),
        api.get('/teams/incognito'),
      ]);
      if (staffRes.status === 'fulfilled') setStaff(staffRes.value.data); else setLoadError(true);
      if (chatsRes.status === 'fulfilled') setChats(chatsRes.value.data); else setLoadError(true);
      if (incogRes.status === 'fulfilled') setIncognito(incogRes.value.data.incognito);
    } catch(err) { console.error("Operation failed:", err); setLoadError(true); }
    setLoading(false);
  };

  const loadMessages = async (chatId) => {
    try {
      const res = await api.get(`/teams/chats/${chatId}/messages`);
      setMessages(res.data);
      setMessagesError(false);
    } catch(err) { console.error("Operation failed:", err); setMessagesError(true); }
  };

  const handleStartChat = async (staffMember) => {
    try {
      const res = await api.post('/teams/chats', {
        staff_email: staffMember.email,
        message: '',
        source: 'staff',
      });
      const newChat = { id: res.data.chat_id, staff_email: staffMember.email, sender_name: staffMember.full_name, status: 'active' };
      setActiveChat(newChat);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start chat');
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !activeChat) return;
    setSending(true);
    try {
      await api.post(`/teams/chats/${activeChat.id}/messages`, { message: newMessage });
      setNewMessage('');
      loadMessages(activeChat.id);
    } catch { toast.error('Failed to send'); }
    setSending(false);
  };

  const handleToggleIncognito = async () => {
    try {
      const res = await api.post('/teams/incognito', { enabled: !incognito });
      setIncognito(res.data.incognito);
      toast.success(res.data.incognito ? 'Incognito mode enabled — you appear offline to customers' : 'Incognito mode disabled — you\'re visible');
    } catch { toast.error('Failed to toggle incognito'); }
  };

  const handleConvertToTicket = async (chatId) => {
    try {
      const res = await api.post(`/teams/chats/${chatId}/to-ticket`);
      toast.success(`Ticket #${res.data.ticket_number} created from chat`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create ticket'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-180px)] gap-3 lg:gap-4" data-testid="teams-chat-page">
      {/* Left: Staff + Chats */}
      <div className="w-full lg:w-80 flex flex-col gap-3 shrink-0 max-h-[42vh] lg:max-h-none lg:h-full" data-testid="teams-chat-sidebar">
        {loadError && (
          <div className="flex items-center justify-between gap-2 text-xs px-3 py-2 rounded-md border border-red-300 bg-red-50 text-red-700" data-testid="teams-chat-load-error">
            <span>Couldn't load team/chat list (network or server issue).</span>
            <Button variant="outline" size="sm" className="h-6 text-xs shrink-0" onClick={loadData} data-testid="teams-chat-retry-btn">Retry</Button>
          </div>
        )}
        {/* Incognito toggle */}
        <Card>
          <CardContent className="p-3 flex items-center justify-between">
            <span className="text-sm font-medium">My Status</span>
            <Button variant={incognito ? 'default' : 'outline'} size="sm" onClick={handleToggleIncognito} data-testid="incognito-toggle">
              {incognito ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
              {incognito ? 'Incognito' : 'Visible'}
            </Button>
          </CardContent>
        </Card>

        {/* Online Staff */}
        <Card className="flex-1 overflow-hidden">
          <CardHeader className="p-3 pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4" /> Team ({staff.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-2 space-y-1 overflow-y-auto max-h-48">
            {staff.map(s => (
              <button
                key={s.email}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left text-sm transition-colors"
                onClick={() => handleStartChat(s)}
                data-testid={`staff-${s.email}`}
              >
                <span className="relative">
                  <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                    {(s.full_name || s.email).charAt(0).toUpperCase()}
                  </div>
                  <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${presenceColor[s.presence] || presenceColor.unknown}`} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-xs truncate">{s.full_name || s.email}</p>
                  <p className="text-[10px] text-gray-500">{presenceLabel[s.presence] || s.presence}</p>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Recent Chats */}
        <Card className="flex-1 overflow-hidden">
          <CardHeader className="p-3 pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><MessageCircle className="h-4 w-4" /> Chats ({chats.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-2 space-y-1 overflow-y-auto">
            {chats.map(c => (
              <button
                key={c.id}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded text-left text-sm transition-colors ${activeChat?.id === c.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-100'}`}
                onClick={() => setActiveChat(c)}
              >
                <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs font-bold shrink-0">
                  {(c.sender_name || c.staff_email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-xs truncate">{c.sender_name || c.staff_email}</p>
                  <p className="text-[10px] text-gray-500 truncate">{c.last_message || 'No messages'}</p>
                </div>
                <Badge variant="secondary" className="text-[9px] shrink-0">{c.source}</Badge>
                <button
                  className="p-0.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm('Delete this chat and all messages?')) {
                      api.delete(`/teams/chats/${c.id}`).then(() => {
                        if (activeChat?.id === c.id) setActiveChat(null);
                        loadData();
                        toast.success('Chat deleted');
                      }).catch(() => toast.error('Failed to delete'));
                    }
                  }}
                  title="Delete chat"
                  data-testid={`delete-chat-${c.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </button>
            ))}
            {chats.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No chats yet</p>}
          </CardContent>
        </Card>
      </div>

      {/* Right: Chat Window */}
      <Card className="flex-1 min-w-0 w-full flex flex-col overflow-hidden" data-testid="teams-chat-window">
        {activeChat ? (
          <>
            <CardHeader className="p-3 border-b flex-row items-center justify-between shrink-0 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
                  {(activeChat.sender_name || activeChat.staff_email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-sm truncate">{activeChat.sender_name || activeChat.staff_email}</CardTitle>
                  <p className="text-[10px] text-gray-500 truncate">{activeChat.sender_email || activeChat.staff_email}</p>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={() => handleConvertToTicket(activeChat.id)} aria-label="Convert chat to ticket" data-testid="convert-to-ticket-btn">
                  <Ticket className="h-3 w-3 sm:mr-1" /> <span className="hidden sm:inline">To Ticket</span>
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setActiveChat(null)} aria-label="Close chat" data-testid="close-chat-btn">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="flex-1 p-3 overflow-y-auto space-y-2">
              {messagesError && (
                <div className="flex items-center justify-between gap-2 text-xs px-3 py-2 rounded-md border border-red-300 bg-red-50 text-red-700" data-testid="teams-chat-messages-error">
                  <span>Couldn't load messages for this chat.</span>
                  <Button variant="outline" size="sm" className="h-6 text-xs shrink-0" onClick={() => loadMessages(activeChat.id)} data-testid="teams-chat-messages-retry-btn">Retry</Button>
                </div>
              )}
              {messages.map(m => {
                const isMe = m.sender_email === user?.email;
                return (
                  <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-lg px-3 py-2 ${isMe ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
                      {!isMe && <p className="text-[10px] font-semibold mb-0.5">{m.sender_name}</p>}
                      <p className="text-sm">{m.message}</p>
                      <p className={`text-[9px] mt-0.5 ${isMe ? 'text-blue-200' : 'text-gray-400'}`}>
                        {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </CardContent>

            <div className="p-3 border-t shrink-0">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  className="min-w-0 flex-1"
                  data-testid="chat-input"
                />
                <Button onClick={handleSend} disabled={sending || !newMessage.trim()} className="shrink-0" aria-label="Send message" data-testid="chat-send-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Select a team member or chat to begin</p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
